import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiErrors, Loaders } from 'api/types';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { History } from 'history';
import { setTab } from 'components/NavBar/productFormTabs';
import { productsPrefix } from 'routes/routes';
import { Product } from 'features/Products/productsTypes';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

type ProductFormState = {
  apiErrors: ApiErrors;
  loaders: Loaders;
  product?: Product | undefined;
};

export const fetchProduct = createAsyncThunk<Product, { history: History; productId: string }, { rejectValue: string }>(
  'productForm/fetchProduct',
  async ({ productId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/products/${productId}`);
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('products.you cannot update this product'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('products.cannot fetch product'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('products.no product found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

interface SaveProductInput {
  history?: History;
  id?: string;
  nextTab?: number;
  product: {
    batch_type?: string;
    description?: string;
    item_code?: string;
    name?: string;
    production_sites_ids?: string[];
  };
}

interface SaveProductPropertiesInput {
  id: string;
  properties: {
    label: string;
    measure: string;
    value: string;
  }[];
}

export const saveProduct = createAsyncThunk<Product, SaveProductInput, { rejectValue: string }>(
  'productForm/saveProduct',
  async ({ product, id, history, nextTab }, { dispatch, rejectWithValue }) => {
    try {
      let response;
      if (id) {
        response = await axiosInstance.put(`/products/${id}`, product);
      } else {
        response = await axiosInstance.post('/products', product);
      }
      const { data } = response;
      if (nextTab && history && !id) {
        dispatch(setTab(1));
        history.replace(`${productsPrefix}/${data.id}/details`);
        history.push(`${productsPrefix}/${data.id}/properties`);
      }
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('products.you cannot update this product'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('products.cannot save product'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('products.no product found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const saveProductProperties = createAsyncThunk<Product, SaveProductPropertiesInput, { rejectValue: string }>(
  'productForm/saveProductProperties',
  async ({ properties, id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/products/${id}/properties`, { properties });

      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case UNAUTHORIZED:
        case FORBIDDEN:
          return rejectWithValue(i18n.t('products.you cannot update this product'));
        case UNPROCESSABLE_ENTITY:
          return rejectWithValue(i18n.t('products.cannot save product'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('products.no product found'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const initialState: ProductFormState = {
  apiErrors: {},
  loaders: {}
};

const productsSlice = createSlice({
  initialState,
  name: 'productForm',
  reducers: {
    resetProduct: () => initialState,
    setProduct: (state, { payload }: PayloadAction<Product | undefined>) => {
      state.product = payload;
    },
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchProduct.fulfilled, (state, { payload }: PayloadAction<Product>) => {
        state.product = payload;
        state.loaders.isLoading = false;
      })
      .addCase(fetchProduct.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchProduct.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(saveProduct.fulfilled, (state, { payload }: PayloadAction<Product>) => {
        state.product = payload;
        state.loaders.isSaving = false;
      })
      .addCase(saveProduct.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveProduct.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(saveProductProperties.fulfilled, (state, { payload }: PayloadAction<Product>) => {
        state.product = payload;
        state.loaders.isSaving = false;
      })
      .addCase(saveProductProperties.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveProductProperties.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = productsSlice;
export { reducer as productForm };
export const { closeError, resetProduct, setProduct } = actions;

export const getProduct = (state: RootState) => state.productForm.product;
export const getProductFormLoaders = (state: RootState) => state.productForm.loaders;
export const getProductFormApiErrors = (state: RootState) => state.productForm.apiErrors;
